import { all, put, takeEvery } from 'redux-saga/effects'

import {
  getClientStateData,
  setClientStateData,
} from 'app/services/api/client-state.service'
import appLoginService from 'app/services/core/app-login.service'
import EntitlementService from 'app/services/core/entitlement.service'
import promiseHandler from 'app/utils/promise-handler'

import { GRADE_KEY } from 'app/constants/client-state.constants'
import { LOCAL_STORAGE_KEYS } from 'app/constants/local-storage.constant'
import {
  userDataActionCreators,
  userDataActions,
} from 'app/store/actions/user-data.actions'
import { GRADES } from 'app/types/grades.types'
import { ReduxSagaAction } from 'app/types/store.types'

export function* getUserData(): Generator<unknown, void, any> {
  try {
    const [loginError, userData] = yield promiseHandler(
      appLoginService.performLogin()
    )

    if (loginError) throw new Error('Login Error')

    const {
      grades,
      userSession,
      hubPrograms,
      filters,
      userEnrollment,
      userDistrictId,
    } = userData
    // this will display "you don’t have access to the Hub" screen, like the login error above
    if (!grades.length) throw new Error('No Grades Error')

    // this method picks user's current grade whether from local storage
    // or from the list of all user's grades
    const currentGrade = EntitlementService.getCurrentGrade(grades)
    const { contentToken, ...rest } = userSession

    let gradeError
    let gradeData
    if (userSession.isReadOnly) {
      gradeData = { grade: currentGrade }
    } else {
      ;[gradeError, gradeData] = yield promiseHandler(
        getClientStateData(GRADE_KEY)
      )
    }

    // Only use the grade from client state if it is entitled to the user.
    if (!gradeError && gradeData.grade && grades.includes(gradeData.grade)) {
      yield put(userDataActionCreators.setUserGrade.success(gradeData.grade))
    } else if (grades.includes(currentGrade)) {
      // if there is no saved grade in client-state, use grade we selected before
      yield put(userDataActionCreators.setUserGrade.success(currentGrade))
    } else {
      // default to the first grade in the user's entitlements.
      yield put(userDataActionCreators.setUserGrade.success(grades[0]))
    }

    yield put(
      userDataActionCreators.getUserData.success({
        grades,
        kcInfo: { ...rest },
        hubPrograms,
        filters,
        contentToken,
        userEnrollment,
        userDistrictId,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(
      userDataActionCreators.getUserData.failure({
        error,
      })
    )
  }
}

interface GradePayload {
  grade: GRADES
  isReadOnly: boolean
}

function* setUserGrade(action: ReduxSagaAction<GradePayload>) {
  const { grade, isReadOnly } = action.payload

  try {
    if (!isReadOnly) {
      yield setClientStateData(GRADE_KEY, { grade })
    }
    localStorage.setItem(LOCAL_STORAGE_KEYS.GRADE, grade)
    yield put(userDataActionCreators.setUserGrade.success(grade))
  } catch (err) {
    console.error(err)
    yield put(userDataActionCreators.setUserGrade.failure(err))
  }
}

export default function* watchUserActions() {
  yield all([
    takeEvery(userDataActions.GET_USER_DATA.REQUEST, getUserData),
    takeEvery(userDataActions.SET_USER_GRADE.REQUEST, setUserGrade),
  ])
}
